/* PlayerManagementPanel — Dedicated dashboard for managing players
   in an Open Play session OR a tournament. Focused on:
   - Payment reconciliation (proof carousel + amount input + running balance)
   - Collapsible account details (games, wins/losses, phone/email, org)
   - Registration timestamps, avatars, club/org labels

   Props:
     mode: 'open_play' | 'tournament'
     eventId: session/tournament id
     players: array (raw player rows; must include amount_due/amount_paid/proof_url/payment_history)
     onRefresh: async fn to refetch after payment action
     onFocusPlayer: (player) => void  // open the roster view / player profile
*/
import { useEffect, useMemo, useState } from 'react';
import { sportsApi } from '../SportsAuthContext';
import { toast } from 'sonner';
import { useBackdropClose } from '../utils/backdropClose';
import {
  ChevronDown, ChevronRight, ChevronLeft, X, Wallet, Camera,
  CheckCircle2, TrendingUp, Clock, User, Mail, Phone, Link2, Landmark,
} from 'lucide-react';

// Money still owed is routine, not an alarm: amber (console colour rules 2026-09-27).
const OWE = '#F59E0B';

const PAY_COLORS = {
  paid: '#22C55E', deposit_paid: '#3B82F6', partial: '#8B5CF6',
  pending: '#F59E0B', awaiting_payment: '#8B5CF6', at_venue: '#737373', free: '#404040', transferred: '#525252',
};
const PAY_LABELS = {
  paid: 'PAID', deposit_paid: '50% PAID', partial: 'PARTIAL',
  pending: 'PROOF ⏳', awaiting_payment: 'ONLINE ⏳', at_venue: 'AT VENUE', free: 'FREE', transferred: 'MOVED WITH SPOT',
};

function money(n) {
  const v = Number(n || 0);
  return `₱${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function fmtDateShort(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

function PayBadge({ status }) {
  if (!status) return null;
  const c = PAY_COLORS[status] || '#737373';
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${c}22`, color: c }}>
      {PAY_LABELS[status] || status.toUpperCase()}
    </span>
  );
}

/* ── Payment Reconciliation Modal ────────────────────────────────────── */
export function PaymentReconcileModal({ mode, eventId, playerIndex, players, onClose, onSaved,
                                        initial = null, onSendLink = null }) {
  const [idx, setIdx] = useState(playerIndex);
  const [amount, setAmount] = useState(initial?.amount != null ? String(initial.amount) : '');
  const [method, setMethod] = useState(initial?.method || 'digital');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const backdrop = useBackdropClose(onClose);

  const p = players[idx];
  const amountDue = Number(p?.amount_due || 0);
  const amountPaid = Number(p?.amount_paid || 0);
  const waived = Number(p?.waived_amount || 0);
  const balance = Math.max(0, amountDue - amountPaid - waived);
  const history = p?.payment_history || [];

  const next = () => { setIdx(i => Math.min(players.length - 1, i + 1)); setAmount(''); setNote(''); };
  const prev = () => { setIdx(i => Math.max(0, i - 1)); setAmount(''); setNote(''); };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!(amt >= 0)) { toast.error('Enter a valid amount (0 or greater)'); return; }
    setSaving(true);
    try {
      const url = mode === 'tournament'
        ? `/sports/tournaments/${eventId}/teams/${p.id}/receive-payment`
        : `/sports/open-play/${eventId}/players/${p.id}/receive-payment`;
      const res = await sportsApi.post(url, { amount: amt, method, note });
      toast.success(res.data?.payment_status === 'paid' ? 'Marked fully paid ✓' : 'Payment recorded');
      setAmount(''); setNote('');
      await onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  if (!p) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}
      {...backdrop} data-testid="payment-reconcile-modal">
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden" style={{ background: '#0F0F0F', border: '1px solid #262626', maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ background: '#141414', borderBottom: '1px solid #262626' }}>
          <div className="flex items-center gap-3 min-w-0">
            {p.avatar_url ? (
              <img src={p.avatar_url} alt={p.name || p.team_name} className="h-9 w-9 rounded-full object-cover" style={{ border: '1px solid #333' }} />
            ) : (
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: '#262626', color: '#A3A3A3' }}>
                {(p.name || p.team_name || '?')[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-black uppercase truncate" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
                {p.name || p.team_name}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <PayBadge status={p.payment_status} />
                {p.org_label && (
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                    style={{ background: '#3B82F618', color: '#3B82F6' }}>{p.org_label}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {players.length > 1 && (
              <span className="text-xs font-bold" style={{ color: '#525252' }}>{idx + 1} / {players.length}</span>
            )}
            <button onClick={onClose} data-testid="reconcile-close" style={{ color: '#A3A3A3' }} className="p-1 rounded hover:bg-neutral-800">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-0" style={{ maxHeight: 'calc(92vh - 60px)', overflowY: 'auto' }}>
          {/* LEFT — Proof carousel */}
          <div className="p-4" style={{ background: '#0A0A0A', borderRight: '1px solid #1C1C1C' }}>
            <p className="text-xs font-bold uppercase mb-2" style={{ color: '#737373' }}>Proof of Payment</p>
            <div className="rounded-lg overflow-hidden relative" style={{ background: '#000', minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {p.proof_url ? (
                <a href={p.proof_url} target="_blank" rel="noreferrer" data-testid="proof-image-link">
                  <img src={p.proof_url} alt="Payment proof" style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }} />
                </a>
              ) : (
                <div className="text-center py-8 px-4">
                  <Camera className="h-8 w-8 mx-auto mb-2" style={{ color: '#333' }} />
                  <p className="text-xs" style={{ color: '#525252' }}>No screenshot uploaded</p>
                  <p className="text-[11px] mt-1" style={{ color: '#525252' }}>You can still record cash / walk-in payments below.</p>
                </div>
              )}
              {players.length > 1 && (
                <>
                  <button onClick={prev} disabled={idx === 0} data-testid="reconcile-prev"
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full"
                    style={{ background: 'rgba(0,0,0,0.7)', color: idx === 0 ? '#333' : '#fff', border: '1px solid #2A2A2A' }}>
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button onClick={next} disabled={idx === players.length - 1} data-testid="reconcile-next"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full"
                    style={{ background: 'rgba(0,0,0,0.7)', color: idx === players.length - 1 ? '#333' : '#fff', border: '1px solid #2A2A2A' }}>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* RIGHT — Amount input + history */}
          <div className="p-4 space-y-3">
            {/* Balance overview */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg p-2 text-center" style={{ background: '#141414', border: '1px solid #262626' }}>
                <p className="text-[10px] uppercase" style={{ color: '#737373' }}>Due</p>
                <p className="text-sm font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{money(amountDue)}</p>
              </div>
              <div className="rounded-lg p-2 text-center" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <p className="text-[10px] uppercase" style={{ color: '#22C55E' }}>Paid</p>
                <p className="text-sm font-black" style={{ color: '#22C55E', fontFamily: 'Oswald, sans-serif' }}>{money(amountPaid)}</p>
              </div>
              <div className="rounded-lg p-2 text-center"
                style={{ background: balance > 0 ? 'rgba(245,158,11,0.08)' : '#141414',
                         border: `1px solid ${balance > 0 ? 'rgba(245,158,11,0.3)' : '#262626'}` }}>
                <p className="text-[10px] uppercase" style={{ color: balance > 0 ? OWE : '#525252' }}>Balance</p>
                <p className="text-sm font-black" style={{ color: balance > 0 ? OWE : '#22C55E', fontFamily: 'Oswald, sans-serif' }}>{money(balance)}</p>
              </div>
            </div>
            {waived > 0 && (
              <p className="text-[11px] px-1" style={{ color: '#8B5CF6' }} data-testid="reconcile-waived">
                Comped {money(waived)}{p.waive_reason ? ` — ${p.waive_reason}` : ''}
              </p>
            )}

            {/* Receive Payment form */}
            <div className="rounded-lg p-3" style={{ background: '#141414', border: '1px solid #262626' }}>
              <p className="text-xs font-bold uppercase mb-2" style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>
                <Wallet className="h-3.5 w-3.5 inline mr-1" /> Receive Payment
              </p>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase font-bold" style={{ color: '#737373' }}>Amount received</label>
                    <input data-testid="reconcile-amount" type="number" step="0.01" min="0" value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder={String(balance || amountDue || 0)}
                      className="w-full mt-1 px-3 py-2 rounded-lg text-sm font-bold"
                      style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff' }} />
                  </div>
                  <div style={{ width: 130 }}>
                    <label className="text-[10px] uppercase font-bold" style={{ color: '#737373' }}>Method</label>
                    <select data-testid="reconcile-method" value={method} onChange={e => setMethod(e.target.value)}
                      className="w-full mt-1 px-2 py-2 rounded-lg text-xs"
                      style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff' }}>
                      <option value="digital">Digital</option>
                      <option value="gcash">GCash</option>
                      <option value="maya">Maya</option>
                      <option value="bank">Bank</option>
                      <option value="cash">Cash</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {[balance, amountDue, Math.round(amountDue / 2), 0].filter((v, i, a) => v >= 0 && a.indexOf(v) === i).slice(0, 4).map(v => (
                    <button key={v} type="button" onClick={() => setAmount(String(v))}
                      data-testid={`reconcile-quick-${v}`}
                      className="text-[10px] font-bold px-2 py-1 rounded-full"
                      style={{ background: '#1C1C1C', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                      {money(v)}
                    </button>
                  ))}
                </div>
                <input data-testid="reconcile-note" type="text" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-xs"
                  style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff' }} />
                <button data-testid="reconcile-submit" onClick={submit} disabled={saving || !amount}
                  className="w-full py-2.5 rounded-lg text-sm font-black uppercase"
                  style={{ background: '#22C55E', color: '#000', opacity: (saving || !amount) ? 0.5 : 1, fontFamily: 'Oswald, sans-serif' }}>
                  {saving ? 'Recording…' : <>✓ Accept Payment</>}
                </button>
                {onSendLink && balance > 0 && (
                  <button data-testid="reconcile-sendlink" type="button"
                    onClick={() => { onClose(); onSendLink(p); }}
                    className="w-full py-2 rounded-lg text-xs font-bold uppercase flex items-center justify-center gap-1.5"
                    style={{ background: '#141414', color: '#00BCD4', border: '1px solid #00BCD440' }}>
                    <Link2 className="h-3.5 w-3.5" /> Send online payment link instead
                  </button>
                )}
              </div>
            </div>

            {/* Payment history */}
            {history.length > 0 && (
              <div className="rounded-lg p-3" style={{ background: '#141414', border: '1px solid #262626' }}>
                <p className="text-xs font-bold uppercase mb-2" style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>
                  <Clock className="h-3.5 w-3.5 inline mr-1" /> History ({history.length})
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {[...history].reverse().map((h, i) => (
                    <div key={h.id || i} className="flex items-center justify-between text-xs py-1"
                      style={{ borderBottom: i < history.length - 1 ? '1px solid #1C1C1C' : 'none' }}>
                      <div className="min-w-0">
                        <span className="font-bold" style={{ color: '#22C55E' }}>{money(h.amount)}</span>
                        <span className="ml-2 uppercase text-[10px]" style={{ color: '#737373' }}>{h.method}</span>
                        {h.recorded_by && <span className="ml-2 text-[10px]" style={{ color: '#525252' }}>by {h.recorded_by}</span>}
                        {h.tendered != null && (
                          <span className="ml-2 text-[10px]" style={{ color: '#525252' }}>
                            ({money(h.tendered)} tendered · {money(h.change)} change)
                          </span>
                        )}
                        {h.note && <span className="ml-2" style={{ color: '#A3A3A3' }}>· {h.note}</span>}
                      </div>
                      <span className="text-[10px] shrink-0" style={{ color: '#525252' }}>{fmtDateShort(h.recorded_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Balance owed = due − paid − waived. Waivers (organizer comps) count as settled. */
export function balanceOf(p) {
  return Math.max(0, Number(p.amount_due || 0) - Number(p.amount_paid || 0) - Number(p.waived_amount || 0));
}

/* ── Main Panel ────────────────────────────────────────────────────────── */
export default function PlayerManagementPanel({ mode = 'open_play', eventId, players, entryFee,
                                                onRefresh, divisions = null, onSendLink = null }) {
  const [expandedId, setExpandedId] = useState(null);
  const [reconcile, setReconcile] = useState(null); // { players: [...], index: 0 }
  const [query, setQuery] = useState('');
  const [filterPay, setFilterPay] = useState('all'); // all | balance | paid
  const [settlement, setSettlement] = useState(null);

  // Where the money physically sits (event box vs facility drawer) + who collected —
  // same numbers the organizer desk sees at handover.
  const loadSettlement = async () => {
    try {
      const base = mode === 'tournament' ? `/sports/tournaments/${eventId}` : `/sports/open-play/${eventId}`;
      const r = await sportsApi.get(`${base}/settlement`);
      setSettlement(r.data);
    } catch { setSettlement(null); }
  };
  useEffect(() => {
    loadSettlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, eventId, players]);

  // Normalize player list — mode 'tournament' uses team.team_name + team.players[]
  const rows = useMemo(() => {
    const norm = (players || []).map(p => {
      if (mode === 'tournament') {
        const primary = (p.players || [])[0] || {};
        return {
          ...p,
          _name: p.team_name || primary.name || 'Team',
          _email: primary.email || '',
          _phone: primary.phone || '',
          _avatar_url: p.logo_thumb_url || p.logo_url,
          _members: p.players || [],
        };
      }
      return {
        ...p,
        _name: p.name,
        _email: p.email,
        _phone: p.phone,
        _avatar_url: p.avatar_url,
        _members: null,
      };
    });
    return norm.filter(p => {
      const q = query.trim().toLowerCase();
      if (q && !(p._name || '').toLowerCase().includes(q)
        && !(p._email || '').toLowerCase().includes(q)
        && !(p._phone || '').toLowerCase().includes(q)) return false;
      if (filterPay === 'balance') {
        if (balanceOf(p) <= 0 && p.payment_status !== 'pending') return false;
      }
      if (filterPay === 'paid' && p.payment_status !== 'paid') return false;
      return true;
    });
  }, [players, query, filterPay, mode]);

  const totals = useMemo(() => {
    const totalDue = rows.reduce((s, p) => s + Number(p.amount_due || 0), 0);
    const totalPaid = rows.reduce((s, p) => s + Number(p.amount_paid || 0), 0);
    const totalWaived = rows.reduce((s, p) => s + Number(p.waived_amount || 0), 0);
    const totalBalance = rows.reduce((s, p) => s + balanceOf(p), 0);
    return { totalDue, totalPaid, totalWaived, totalBalance };
  }, [rows]);

  const openReconcile = (player) => {
    // Build list of rows that can accept a payment (skip 'free' and fully-paid non-partial)
    const payable = rows.filter(r => (r.payment_status !== 'free'));
    const idx = Math.max(0, payable.findIndex(r => r.id === player.id));
    setReconcile({ players: payable, index: idx });
  };

  return (
    <div className="space-y-3" data-testid="player-mgmt-panel">
      {/* Totals + filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-lg p-2.5" style={{ background: '#141414', border: '1px solid #262626' }}>
          <p className="text-[10px] uppercase" style={{ color: '#737373' }}>Players</p>
          <p className="text-lg font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{rows.length}</p>
        </div>
        <div className="rounded-lg p-2.5" style={{ background: '#141414', border: '1px solid #262626' }}>
          <p className="text-[10px] uppercase" style={{ color: '#737373' }}>Expected</p>
          <p className="text-lg font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{money(totals.totalDue)}</p>
        </div>
        <div className="rounded-lg p-2.5" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
          <p className="text-[10px] uppercase" style={{ color: '#22C55E' }}>Collected</p>
          <p className="text-lg font-black" style={{ color: '#22C55E', fontFamily: 'Oswald, sans-serif' }}>{money(totals.totalPaid)}</p>
        </div>
        <div className="rounded-lg p-2.5"
          style={{ background: totals.totalBalance > 0 ? 'rgba(245,158,11,0.08)' : '#141414',
                   border: `1px solid ${totals.totalBalance > 0 ? 'rgba(245,158,11,0.3)' : '#262626'}` }}>
          <p className="text-[10px] uppercase" style={{ color: totals.totalBalance > 0 ? OWE : '#737373' }}>Outstanding</p>
          <p className="text-lg font-black" style={{ color: totals.totalBalance > 0 ? OWE : '#22C55E', fontFamily: 'Oswald, sans-serif' }}>{money(totals.totalBalance)}</p>
        </div>
      </div>
      {totals.totalWaived > 0 && (
        <p className="text-[11px] px-1" style={{ color: '#8B5CF6' }} data-testid="pm-comped-total">
          {money(totals.totalWaived)} comped/waived — not counted as outstanding
        </p>
      )}

      {/* Where the money physically sits + who collected it (from the ledger) */}
      {settlement && (Number(settlement.collected_event_box) > 0 || Number(settlement.collected_facility) > 0) && (
        <div className="rounded-xl p-3" style={{ background: '#0F0F0F', border: '1px solid #262626' }} data-testid="pm-settlement">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs font-bold uppercase flex items-center gap-1.5" style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>
              <Landmark className="h-3.5 w-3.5" /> Where the money sits
            </p>
            <div className="flex gap-4 text-xs" style={{ color: '#737373' }}>
              <span>This event's box <b style={{ color: '#fff' }}>{money(settlement.collected_event_box)}</b></span>
              <span>Front desk drawer <b style={{ color: '#fff' }}>{money(settlement.collected_facility)}</b></span>
            </div>
          </div>
          {settlement.by_collector && Object.keys(settlement.by_collector).length > 0 && (
            <div className="flex gap-1.5 flex-wrap mt-2">
              {Object.entries(settlement.by_collector).map(([who, v]) => (
                <span key={who} className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ background: '#1C1C1C', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                  {who} · <b style={{ color: '#22C55E' }}>{money(v.amount)}</b> ({v.count})
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <input data-testid="pm-search" type="text" placeholder="Search name, email, phone…"
          value={query} onChange={e => setQuery(e.target.value)}
          className="flex-1 basis-full sm:basis-0 min-w-0 px-3 py-2 rounded-lg text-sm"
          style={{ background: '#141414', border: '1px solid #262626', color: '#fff' }} />
        {['all', 'balance', 'paid'].map(f => (
          <button key={f} onClick={() => setFilterPay(f)} data-testid={`pm-filter-${f}`}
            className="px-3 py-2 rounded-lg text-xs font-bold uppercase"
            style={{
              background: filterPay === f ? '#262626' : '#141414',
              color: filterPay === f ? '#fff' : '#737373',
              border: `1px solid ${filterPay === f ? '#525252' : '#262626'}`,
              fontFamily: 'Oswald, sans-serif',
            }}>
            {f === 'all' ? 'All' : f === 'balance' ? 'Has Balance' : 'Paid'}
          </button>
        ))}
      </div>

      {/* Player rows */}
      <div className="space-y-2">
        {rows.length === 0 ? (
          <div className="text-center py-10 rounded-xl" style={{ background: '#141414', border: '1px solid #262626' }}>
            <User className="h-10 w-10 mx-auto mb-2" style={{ color: '#333' }} />
            <p className="text-sm" style={{ color: '#737373' }}>No players match this filter</p>
          </div>
        ) : rows.map(p => {
          const expanded = expandedId === p.id;
          const balance = balanceOf(p);
          const waived = Number(p.waived_amount || 0);
          const checkedIn = Boolean(p.checked_in_at) || ['active', 'playing', 'resting'].includes(p.status);
          const divName = divisions?.find(d => d.id === p.division_id)?.name;
          const winRate = p.matches_played > 0 ? Math.round((Number(p.wins || 0) / Number(p.matches_played)) * 100) : null;
          return (
            <div key={p.id} data-testid={`pm-row-${p.id}`} className="rounded-xl overflow-hidden"
              style={{ background: '#141414', border: '1px solid #1C1C1C' }}>
              <div className="p-3 flex items-center gap-3">
                {/* Avatar */}
                {p._avatar_url ? (
                  <img src={p._avatar_url} alt={p._name} className="h-10 w-10 rounded-full object-cover shrink-0" style={{ border: '1px solid #333' }} />
                ) : (
                  <div className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                    style={{ background: '#262626', color: '#A3A3A3' }}>
                    {(p._name || '?')[0]?.toUpperCase()}
                  </div>
                )}

                {/* Name / meta */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold" style={{ color: '#fff' }}>{p._name}</span>
                    {p.org_label && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                        style={{ background: '#3B82F618', color: '#3B82F6' }}>{p.org_label}</span>
                    )}
                    <PayBadge status={p.payment_status} />
                    {waived > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" title={p.waive_reason || ''}
                        style={{ background: '#8B5CF622', color: '#8B5CF6' }}>COMPED {money(waived)}</span>
                    )}
                    {checkedIn && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                        style={{ background: '#22C55E18', color: '#22C55E' }}>
                        <CheckCircle2 className="h-3 w-3" /> HERE
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px]" style={{ color: '#737373' }}>
                    <span data-testid={`pm-registered-${p.id}`} className="whitespace-nowrap">{fmtDateShort(p.registered_at)}</span>
                    {divName && <span className="whitespace-nowrap">{divName}</span>}
                    {p.matches_played > 0 && (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <TrendingUp className="h-3 w-3" />
                        {p.matches_played} plays · {p.wins || 0}W / {p.losses || 0}L
                      </span>
                    )}
                    {balance > 0 && <span className="whitespace-nowrap" style={{ color: OWE, fontWeight: 700 }}>Balance {money(balance)}</span>}
                  </div>
                </div>

                {/* Manage payment button */}
                <button data-testid={`pm-pay-${p.id}`} onClick={() => openReconcile(p)}
                  className="px-3 py-2 rounded-lg text-xs font-black uppercase shrink-0"
                  style={{
                    background: balance > 0 ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.12)',
                    color: balance > 0 ? OWE : '#22C55E',
                    border: `1px solid ${balance > 0 ? OWE + '55' : '#22C55E44'}`,
                    fontFamily: 'Oswald, sans-serif',
                  }}>
                  <Wallet className="h-3.5 w-3.5 inline mr-1" />
                  {balance > 0 ? 'Collect' : (p.proof_url ? 'View Proof' : 'Payment')}
                </button>

                {/* Expand */}
                <button data-testid={`pm-expand-${p.id}`} onClick={() => setExpandedId(expanded ? null : p.id)}
                  className="p-2 rounded-lg shrink-0" style={{ color: '#A3A3A3', background: '#0A0A0A' }}>
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>

              {expanded && (
                <div className="px-4 pb-4 pt-1 grid sm:grid-cols-2 gap-4 text-xs" style={{ background: '#0F0F0F', borderTop: '1px solid #1C1C1C' }}>
                  <div>
                    <p className="text-[10px] uppercase font-bold mb-1" style={{ color: '#525252' }}>Contact</p>
                    <div className="space-y-1">
                      {p._email && <p style={{ color: '#A3A3A3' }}><Mail className="h-3 w-3 inline mr-1" />{p._email}</p>}
                      {p._phone && !String(p._phone).startsWith('rsvp-') && (
                        <p style={{ color: '#A3A3A3' }}><Phone className="h-3 w-3 inline mr-1" />{p._phone}</p>
                      )}
                      {!p._email && !p._phone && <p style={{ color: '#525252' }}>—</p>}
                    </div>
                    {p._members && p._members.length > 1 && (
                      <>
                        <p className="text-[10px] uppercase font-bold mt-3 mb-1" style={{ color: '#525252' }}>Team Members</p>
                        {p._members.map((m, i) => (
                          <p key={i} style={{ color: '#A3A3A3' }}>· {m.name}{m.email ? ` (${m.email})` : ''}</p>
                        ))}
                      </>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] uppercase font-bold mb-1" style={{ color: '#525252' }}>Stats</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg p-2 text-center" style={{ background: '#141414' }}>
                        <p className="text-[9px] uppercase" style={{ color: '#525252' }}>Plays</p>
                        <p className="text-sm font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{p.matches_played || 0}</p>
                      </div>
                      <div className="rounded-lg p-2 text-center" style={{ background: '#141414' }}>
                        <p className="text-[9px] uppercase" style={{ color: '#22C55E' }}>Wins</p>
                        <p className="text-sm font-black" style={{ color: '#22C55E', fontFamily: 'Oswald, sans-serif' }}>{p.wins || 0}</p>
                      </div>
                      <div className="rounded-lg p-2 text-center" style={{ background: '#141414' }}>
                        <p className="text-[9px] uppercase" style={{ color: '#EF4444' }}>Losses</p>
                        <p className="text-sm font-black" style={{ color: '#EF4444', fontFamily: 'Oswald, sans-serif' }}>{p.losses || 0}</p>
                      </div>
                    </div>
                    {winRate !== null && (
                      <p className="mt-2" style={{ color: '#A3A3A3' }}>Win rate: <b style={{ color: '#fff' }}>{winRate}%</b></p>
                    )}
                  </div>

                  {(p.payment_history || []).length > 0 && (
                    <div className="sm:col-span-2">
                      <p className="text-[10px] uppercase font-bold mb-1" style={{ color: '#525252' }}>Payment History</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {[...(p.payment_history || [])].reverse().map((h, i) => (
                          <div key={h.id || i} className="flex items-center justify-between py-1"
                            style={{ borderBottom: '1px solid #1C1C1C' }}>
                            <span>
                              <b style={{ color: '#22C55E' }}>{money(h.amount)}</b>
                              <span className="ml-2 uppercase text-[10px]" style={{ color: '#737373' }}>{h.method}</span>
                              {h.recorded_by && <span className="ml-2 text-[10px]" style={{ color: '#525252' }}>by {h.recorded_by}</span>}
                              {h.tendered != null && (
                                <span className="ml-2 text-[10px]" style={{ color: '#525252' }}>
                                  ({money(h.tendered)} tendered · {money(h.change)} change)
                                </span>
                              )}
                              {h.note && <span className="ml-2" style={{ color: '#A3A3A3' }}>· {h.note}</span>}
                            </span>
                            <span style={{ color: '#525252' }}>{fmtDateShort(h.recorded_at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {reconcile && (
        <PaymentReconcileModal
          mode={mode}
          eventId={eventId}
          players={reconcile.players}
          playerIndex={reconcile.index}
          onSendLink={onSendLink}
          onClose={() => setReconcile(null)}
          onSaved={async () => {
            if (onRefresh) await onRefresh();
            loadSettlement();
          }}
        />
      )}
    </div>
  );
}
